import Message from "../components/Message/Message";
import {
  conversionRates,
  CurrencyType,
  DISCOUNT_PERCENTAGE,
  MONTHS,
  PLAN_DETAILS
} from "./constants";

import config from "../config";
import { endShadowSession, isShadowing } from "./shadowSession";
import { planAllowsFeature, planFeatureLimit } from "./planLimits";
import Toast from "../components/Toast/Toast";
import React from "react";
import moment, { Moment } from "moment";

/**
 * The caller's suffix, appended only where it still makes sense.
 *
 * Callers pass things like ". Please retry." for the usual case of a request
 * that failed and might work next time. But an API message that already ends
 * in a full stop then reads "add more.. Please retry." — and for a refusal the
 * advice is wrong as well as ugly: retrying a plan limit or a permission error
 * gives the same answer. So the suffix is dropped when the message already
 * closes itself, and the punctuation is not doubled when it does not.
 */
function withErrorSuffix(message: string, extraMessage: string) {
  const text = String(message).trim();
  if (!extraMessage) return text;
  // A message the API wrote as a full sentence is already complete advice.
  if (/[.!?]$/.test(text)) return text;
  return text + extraMessage;
}

function generateErrorMessage(error: any, extraMessage: string = "") {
  return error && error.message ? (
    <Message
      message={withErrorSuffix(error.message, extraMessage)}
      type="error"
      icon="fa-exclamation"
    />
  ) : null;
}

function getError(error: any, extraMessage: string = "") {
  return error && error.message ? withErrorSuffix(error.message, extraMessage) : null;
}

function generateMessage(message: any, type: string) {
  return message ? <Message message={message} type={type} /> : null;
}

function generateInfoMessage(message: any) {
  return message ? (
    <div className="mb-4 rounded-lg bg-gray-100 px-4 py-3 text-center text-sm font-medium text-gray-700">
      {message}
    </div>
  ) : null;
}

function isUserAccountAdmin(store: any) {
  return user(store)?.role === "account-admin";
}

function isUserAccountUser(store: any) {
  return user(store)?.role === "account-user";
}

export function isUserLandlordOrAccountUser(store: any) {
  return isUserAccountAdmin(store) || isUserAccountUser(store);
}

/**
 * A landlord who owns properties an agency manages.
 *
 * Not to be confused with the agency owner (role 3), whom the UI used to call a
 * landlord too. An agency landlord is a *client* of the agency rather than part
 * of it, and sees only what hangs off their own properties — the API scopes them
 * by landlord_id where agency staff are scoped by agency_id.
 */
function isUserAgencyLandlord(store: any) {
  return user(store)?.role === "agency-landlord";
}

/** Agency staff: the owner and their account users. Not their landlords. */
/**
 * The filter a list request should carry for whoever is being viewed.
 *
 * An agency landlord is scoped by landlord_id; everyone else in an agency by
 * agency_id.
 *
 * A shadow session now carries the shadowed user's own token, so the API can
 * scope from the token alone. These filters stay as a display preference and can
 * be removed page by page.
 *
 * Safe to send either way. The API overwrites it for anyone not entitled to what
 * they asked for, and forces an agency landlord onto their own id.
 */
export function scopeArgs(store: any) {
  const viewing = user(store);
  if (isUserAgencyLandlord(store)) {
    return { landlord_id: viewing?.id };
  }
  return viewing?.agency_id ? { agency_id: viewing.agency_id } : {};
}

export function isUserAgencyStaff(store: any) {
  return isUserAccountAdmin(store) || isUserAccountUser(store);
}

/**
 * Anyone who lives under an agency's plan: the owner, their staff, and their
 * landlords. Tenants are out — the plan is not theirs and they are never billed
 * for it. Used by the plan badge, so it shows for every member of the agency and
 * not just the owner.
 */
export function isUserOnAgencyPlan(store: any) {
  return isUserAccountAdmin(store) || isUserAccountUser(store) || isUserAgencyLandlord(store);
}

function isUserTenant(store: any) {
  return user(store)?.role === "tenant";
}

function isUserAdmin(store: any) {
  return user(store)?.role === "admin";
}

function isUserSales(store: any) {
  // return isUserAdmin(store) && user(store)?.agreed_commission_percentage != null;
  return user(store)?.role === "sales";
}

export function isUserSuperAdmin(store: any) {
  return user(store)?.role === "super-admin";
}

function isUserSuperAdminOrAdmin(store: any) {
  return isUserSuperAdmin(store) || isUserAdmin(store);
}

function user(store: any) {
  if (store?.impersonatingUser) {
    return store?.impersonatingUser;
  } else {
    return store?.user;
  }
}

function profilePicture(store: any) {
  return user(store)?.profile_image ?? null;
}

// Converts any date to SAST (Africa/Johannesburg)
function toSAST(date: string | Date | Moment | undefined): any {
  if (!date) return "";
  return moment.utc(date).utcOffset(120);
}

function humanFormatDateTime(
  date: string | Date | Moment | undefined,
  includeSeconds?: boolean
): string {
  if (!date) return "";
  const momentDate: Moment = toSAST(date);
  return momentDate.format("D MMM YYYY HH:mm" + (includeSeconds ? ":ss" : ""));
}

function humanFormatDate(date: string | Date | Moment | undefined): string {
  if (!date) return "";
  const momentDate: Moment = toSAST(date);
  return momentDate.format("D MMM YYYY");
}

function humanFormatDateWithDay(date: string | Date | Moment | undefined): string {
  if (!date) return "";
  const momentDate: Moment = toSAST(date);
  return momentDate.format("ddd, D MMM YYYY");
}

export function humanFormatDateWithDayAndTime(date: string | Date | Moment | undefined): string {
  if (!date) return "";
  const momentDate: Moment = toSAST(date);
  return momentDate.format("ddd, D MMM YYYY HH:mm");
}

function getMonth(monthValue: number) {
  if (monthValue > -1 && monthValue < 13) {
    return MONTHS[monthValue];
  } else {
    return "Invalid month";
  }
}

function getCurrentMonthEndDate() {
  const now = new Date();
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  // console.log(lastDay);
  return humanFormatDate(lastDay);
}

function titleCase(str: string) {
  if (str) {
    const splitStr = str?.toLowerCase().split(/[\s-_]+/); // Split by space or hyphen
    for (let i = 0; i < splitStr.length; i++) {
      splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
    }
    return splitStr.join(" ");
  } else {
    return str;
  }
}

// Same splitting as titleCase, but only the first word is capitalised:
// "on_hold" becomes "On hold", "AWAITING REVIEW" becomes "Awaiting review".
function sentenceCase(str: string) {
  if (str) {
    const sentence = str
      ?.toLowerCase()
      .split(/[\s-_]+/)
      .join(" ");
    return sentence.charAt(0).toUpperCase() + sentence.substring(1);
  } else {
    return str;
  }
}

function clone(obj: any): any {
  if (!obj) return;
  return JSON.parse(JSON.stringify(obj));
}

function convertArgsToParams(args: Record<string, unknown>): string {
  return Object.entries(args)
    .map(([key, value]) => {
      // Convert value safely to string
      const stringValue =
        typeof value === "string" || typeof value === "number" || typeof value === "boolean"
          ? String(value)
          : "";

      return `${encodeURIComponent(key)}=${encodeURIComponent(stringValue)}`;
    })
    .join("&");
}

const activeRequests: any = {};
let requestIDCounter = 0;

async function killDuplicateRequests(activeRequest: {
  body: any;
  method: string;
  requestID: number;
  url: string;
  requestTimeStamp: Date;
}) {
  let requestIDs: any[] = [];

  if (activeRequests[activeRequest?.url]) {
    requestIDs = Object.keys(activeRequests[activeRequest.url]);
  }

  for (const requestID of requestIDs) {
    const req = activeRequests[activeRequest.url][requestID];
    if (req.requestID === activeRequest.requestID) {
      // Remove request from active requests
      delete activeRequests[activeRequest.url][requestID];
    } else if (
      activeRequest.url === req.url &&
      activeRequest.method === req.method &&
      JSON.stringify(activeRequest.body) === JSON.stringify(req.body)
    ) {
      // // kill all other requests with the same parameters & remove from active requests
      if (activeRequest.requestTimeStamp < req.requestTimeStamp) {
        // Abort only if request was made before the active request and has not yet returned a response
        await req.abortController.abort();
        // console.warn("Aborted duplicate request", `${req.method}: ${req.url}`);
        delete activeRequests[activeRequest.url][requestID];
      }
    }
    if (Object.keys(activeRequests[activeRequest.url]).length === 0) {
      delete activeRequests[activeRequest.url];
    }
  }
}

async function request(
  store: any,
  endpoint: string,
  method: string,
  args?: any,
  headers?: any,
  appendStringArgs?: any,
  // Set only by the retry below, so one 401 can be re-sent on the admin's own
  // token after an expired shadow session is ended. Bounded to a single retry:
  // if the admin's token is dead too, the second 401 goes to the login screen
  // like any other.
  hasRetriedAfterShadow?: boolean
) {
  // const retryCounterValue: number = retryCounter ?? 0;
  // const maxRetries = 3;
  let accessToken: any;
  if (store) {
    accessToken = store?.access_token;
  }
  if (!headers) {
    headers = { "Content-Type": "application/json" };
  }
  method = method.toUpperCase();
  try {
    let body = null;
    let path = endpoint;

    if (method === "GET") {
      if (args) {
        path += "/?" + convertArgsToParams(args) + (appendStringArgs ? `&${appendStringArgs}` : "");
      }
    } else if (args instanceof FormData) {
      // For FormData (file uploads), assign directly without JSON.stringify
      if (args instanceof FormData) {
        delete headers["Content-Type"]; // Don't manually set Content-Type for FormData
      }
      body = args;
    } else {
      body = JSON.stringify(args);
    }

    const url: any = config.baseUrl + path;

    if (accessToken) {
      headers["Authorization"] = "bearer " + accessToken;
    }
    let request: any;

    const init: { method: string; headers: any; body: any; signal?: any } = {
      method: method,
      headers: headers,
      body: body
    };

    if (method === "GET") {
      const requestID = requestIDCounter;
      requestIDCounter++;
      const abortController = new AbortController();
      request = {
        url: url,
        method,
        abortController,
        body,
        requestID,
        requestTimeStamp: new Date()
      };
      if (!activeRequests[url]) {
        activeRequests[url] = {};
      }
      activeRequests[url][requestID] = request;
      init.signal = abortController.signal;
    }

    const response = await fetch(url, init);

    if (method === "GET") {
      killDuplicateRequests(request);
    }

    if (!response.ok) {
      let errorMsg = await response.text();
      let errorDetail: any = null;
      try {
        // Parse the response as JSON
        const errorJson = JSON.parse(errorMsg);

        // Check if the `detail` property exists and use it
        if (errorJson.detail) {
          errorDetail = errorJson.detail;
          // Most errors send a plain string; the subscription gate sends an
          // object, which would otherwise be shown as "[object Object]".
          errorMsg =
            typeof errorDetail === "string" ? errorDetail : (errorDetail.message ?? errorMsg);
        }
      } catch (e) {
        // If parsing fails, fallback to the original error message
        console.error("Error parsing response:", e);
      }

      // A 401 while shadowing usually means the short-lived shadow token has
      // expired, not that the admin's session has. The badge's timer normally
      // ends the session first, but it ticks every fifteen seconds and a click
      // can land in the gap. Ending the session here and re-sending on the
      // admin's own token is what stops that gap logging them out of their own
      // account and losing whatever they had open.
      if (response.status === 401 && !hasRetriedAfterShadow && isShadowing(store)) {
        const restored = endShadowSession(store);
        if (restored) {
          return request(restored, endpoint, method, args, headers, appendStringArgs, true);
        }
      }

      checkTokenValidity(response, endpoint, errorMsg);
      checkSubscriptionRequired(response, errorDetail, store);

      // if (response.status === 504 || response.status === 502) {
      //   if (retryCounterValue < maxRetries) {
      //     return new Promise((resolve) => {
      //       setTimeout(
      //         async () => {
      //           const res = await request(store, endpoint, method, args, headers, retryCounterValue + 1);
      //           resolve(res);
      //         },
      //         1000 * (retryCounterValue + 1)
      //       ); // Progressively longer timeouts as retries progress
      //     });
      //   } else {
      //     console.log("Service unavailable, maximum retries reached.");
      //   }
      // }

      return { ok: false, error: new Error(errorMsg), code: response.status };
    }

    let data = "";
    try {
      data = await response.text();
      data = JSON.parse(data);
    } catch (e) {
      // Assume it is text
    }

    const responseHeaders: any = {};

    response.headers.forEach((value, key) => {
      responseHeaders[key] = value;
    });

    return { ok: true, data, code: response.status, headers: responseHeaders };
  } catch (e: any) {
    // A duplicate GET is aborted on purpose by killDuplicateRequests, so the
    // caller can tell that apart from a request that really failed.
    if (e?.name === "AbortError") {
      return { ok: false, aborted: true, error: getError(e, "") };
    }
    return { ok: false, error: getError(e, "") };
  }
}

/**
 * Send a caller whose session is gone back to the login screen.
 *
 * 401 only. A 403 is the server saying it knows exactly who this is and is
 * refusing anyway — a plan limit reached, an invoice belonging to another
 * agency, an action blocked while shadowing. Signing them out returns them to
 * the same refusal a moment later, having thrown away whatever they had
 * unsaved, and tells them their session broke when it did not. Those messages
 * are for showing, not for redirecting, and every auth failure the API has —
 * missing header, malformed token, expired token — already answers 401.
 */
function checkTokenValidity(response: any, endpoint: string, errorMsg?: string) {
  if (response.status === 401 && endpoint !== "token" && endpoint !== "/token") {
    if (errorMsg?.includes("session has expired")) {
      window.location.href = "/login/?session-expired=True&redirect=" + window.location.pathname;
    } else {
      window.location.href = "/login/?unauthorised=True&redirect=" + window.location.pathname;
    }
  }
}

/** Send an account the API has stopped serving to the screen that can unblock it.
 *
 * The gate answers a write from a lapsed trial or an unpaid account with 402
 * `subscription_required`. Without this the visitor only sees a save that
 * failed, with nothing telling them the trial is over or where to subscribe.
 * The status travels from the response so the screen asks for the right thing.
 */
function checkSubscriptionRequired(response: any, detail: any, store?: any) {
  if (response.status !== 402) return;
  if (window.location.pathname.startsWith("/account-on-hold")) return;
  // Tenants are excluded here for the same reason they are excluded from the
  // on-hold redirect: the screen asks for the agency's subscription, which a
  // tenant of that agency has no way to pay.
  if (user(store)?.role_id === 5) return;

  const accountStatus = typeof detail === "object" ? detail?.account_status : null;
  window.location.href = "/account-on-hold?status=" + (accountStatus ?? "on_hold");
}

function showToast(toastID: string, message: string, type: string) {
  return <Toast toastID={toastID} message={message} type={type} />;
}

function generateID() {
  // Generate a random number between 0 and 99999
  const randomNumber = Math.floor(Math.random() * 100000);
  // Get the current timestamp
  const timestamp = Date.now();
  // Combine the random number and timestamp to create the toast ID
  return `${timestamp}_${randomNumber}`;
}

//PLANS//

/**
 * The plan this user is actually entitled to.
 *
 * The plan belongs to the agency, and only to the agency. It used to be copied
 * onto the owner's user row as well, which gave two places to answer one
 * question and left everybody else — account users, agency landlords — with no
 * plan at all. Every check below read that as the free tier, so paid features
 * were hidden from people whose agency had paid for them.
 *
 * `user.plan_id` no longer exists. The API returns the agency's as
 * effective_plan_id; the fallback is only for a client holding a stale session
 * from before that column went.
 */
export function getPlanID(store: any) {
  return user(store)?.agency_plan_id ?? user(store)?.effective_plan_id ?? user(store)?.plan_id;
}

/**
 * The plan's name, from the agency.
 *
 * `agency_plan_name` is the field. The older name is read only so a client
 * still holding a session from before the rename keeps working.
 */
export function planName(store: any) {
  return user(store)?.agency_plan_name ?? user(store)?.plan_name;
}

export function isFreePlan(store: any) {
  const plan = getPlanID(store);
  return plan ? plan === 1 : true;
}

function isPremiumPlan(store: any) {
  if (getPlanID(store)) {
    return getPlanID(store) === 2;
  } else {
    return false;
  }
}

function isBasicListingPlan(store: any) {
  if (getPlanID(store)) {
    return getPlanID(store) === 5;
  } else {
    return false;
  }
}
function isProListingPlan(store: any) {
  if (getPlanID(store)) {
    return getPlanID(store) === 6;
  } else {
    return false;
  }
}

function isEnterpriseOrPlusPlan(store: any) {
  const planID = getPlanID(store);
  if (planID) {
    return planID === 3 || planID === 4;
  } else {
    return false;
  }
}

export function hasReachedFeatureLimit(store: any, featureName: string, totalCount: number) {
  if (isUserSuperAdminOrAdmin(store) || isEnterpriseOrPlusPlan(store)) {
    return false;
  }
  let limit = 0;
  const featurePlan = planName(store);
  if (featurePlan) {
    limit = getPlanFeatureLimit(featurePlan, featureName);
    return totalCount >= limit;
  } else {
    return true;
  }
}

function hasReachedPropertiesLimit(store: any, totalCount: number) {
  if (isUserSuperAdminOrAdmin(store)) {
    return false;
  }
  let limit = 0;
  if (getPlanID(store)) {
    if (isFreePlan(store)) {
      limit = getPlanFeatureLimit("starter", "properties");
      return totalCount >= limit;
    }
    if (isPremiumPlan(store)) {
      limit = getPlanFeatureLimit("premium", "properties");
      return totalCount >= limit;
    }
    if (isBasicListingPlan(store)) {
      limit = getPlanFeatureLimit("basic-listing", "properties");
      return totalCount >= limit;
    }
    if (isProListingPlan(store)) {
      limit = getPlanFeatureLimit("pro-listing", "properties");
      return totalCount >= limit;
    }
    return !isEnterpriseOrPlusPlan(store);
  } else {
    return true;
  }
}

function hasReachedPropertyGroupLimit(store: any, totalCount: number) {
  if (isUserSuperAdminOrAdmin(store)) {
    return false;
  }
  let limit = 0;
  if (getPlanID(store)) {
    if (isFreePlan(store)) {
      limit = getPlanFeatureLimit("starter", "property-groups");
      return totalCount >= limit;
    }
    if (isPremiumPlan(store)) {
      limit = getPlanFeatureLimit("premium", "property-groups");
      return totalCount >= limit;
    }
    if (isBasicListingPlan(store)) {
      limit = getPlanFeatureLimit("basic-listing", "property-groups");
      return totalCount >= limit;
    }
    if (isProListingPlan(store)) {
      limit = getPlanFeatureLimit("pro-listing", "property-groups");
      return totalCount >= limit;
    }
    return !isEnterpriseOrPlusPlan(store);
  } else {
    return true;
  }
}

function getPlanDetails(store: any, total: number, feature: string) {
  if (isUserAdmin(store)) {
    return null;
  }

  const limit = getPlanLimit(store, feature);

  if (!limit) return null;

  const pct = Math.min((total / limit) * 100, 100);
  const isWarning = pct >= 80;
  const isFull = pct >= 100;

  return (
    <div className="flex items-center gap-2 text-xs text-gray-500">
      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-gray-200">
        <div
          className={`h-full rounded-full transition-all ${isFull ? "bg-red-500" : isWarning ? "bg-amber-400" : "bg-primary-dark"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span
        className={`font-medium tabular-nums ${isFull ? "text-red-500" : isWarning ? "text-amber-500" : "text-gray-500"}`}
      >
        {total}/{limit}
      </span>
    </div>
  );
}

function getPlanLimit(store: any, feature: string) {
  if (isUserSuperAdminOrAdmin(store)) {
    return null;
  }

  const limitPlan = planName(store);
  if (getPlanID(store) && limitPlan) {
    return getPlanFeatureLimit(limitPlan, feature);
  } else {
    return null;
  }
}

function getPlanFeatureLimit(planName: string, featureName: string) {
  // The cap comes from the API's plan settings now, not from a hardcoded array
  // here. -1 still collapses to 0 for the callers that compare with >=, which
  // is why the enterprise tiers are short-circuited before they ever reach this
  // — see isEnterpriseOrPlusPlan in hasReachedFeatureLimit.
  const limit = planFeatureLimit(planName, featureName);
  if (limit === -1) {
    return 0;
  } else {
    return limit;
  }
}

export function isAllowedFeature(store: any, featureName: string) {
  if (isUserSuperAdminOrAdmin(store) || isUserSales(store) || isUserTenant(store)) {
    return true;
  }
  const planName = getPlan(store);

  return planAllowsFeature(planName, featureName);
}

async function getPlanData(planID: number) {
  const result: any = await request({}, `plans/${planID}`, "GET");
  if (result.ok) {
    return result.data;
  }
  return null;
}

async function getPlans() {
  const result: any = await request({}, `plans`, "GET");
  if (result.ok) {
    return result?.data;
  }
  return null;
}

function getPlanFeatureCode(planName: string) {
  return PLAN_DETAILS.find(plan => plan.plan === planName)?.id ?? 0;
}

function getPlanName(store: any) {
  const viewing = user(store);
  // The plan belongs to the agency, so a member who is not the owner has no
  // plan_id of their own — only effective_plan_id, off the agency.
  const planID = viewing?.agency_plan_id ?? viewing?.effective_plan_id ?? viewing?.plan_id;
  const viewingPlan = viewing?.agency_plan_name ?? viewing?.plan_name;
  if (planID && viewingPlan) {
    return titleCase(viewingPlan);
  }
  return "Starter";
}

export function getPlan(store: any) {
  if (user(store) && planName(store)) {
    return planName(store);
  }

  return "";
}

function getPlanFromID(planID: number) {
  if (planID === 1) {
    return "starter";
  } else if (planID === 2) {
    return "premium";
  } else if (planID === 3) {
    return "enterprise";
  } else if (planID === 4) {
    return "enterprise-plus";
  } else if (planID === 5) {
    return "basic-listing";
  } else if (planID === 6) {
    return "pro-listing";
  }

  return "";
}

function validateEmptyField(value: string, fieldName: string): string {
  if (!value) {
    return `Please enter your ${fieldName}`;
  }
  return "";
}

function validateEmail(email: string): string {
  if (!email) {
    return "Please enter your email";
  } else if (!/\S+@\S+\.\S+/.test(email)) {
    return "Please enter a valid email address";
  }
  return "";
}

function validatePassword(password: string): string {
  if (!password) {
    return "Please enter your password";
  } else if (password.length < 8) {
    return "Password must be at least 8 characters long";
  } else if (!/[A-Z]/.test(password)) {
    return "Password must contain at least one capital letter";
  } else if (!/[\W_]/.test(password)) {
    return "Password must contain at least one special character";
  }
  return "";
}

function validateConfirmPassword(confirmPassword: string, password: string) {
  if (!confirmPassword) {
    return "Please confirm your password";
  } else if (confirmPassword !== password) {
    return "Passwords do not match";
  }
  return "";
}

function validatePhone(phone: string): string {
  if (!phone) {
    return "Please enter your phone number";
  } else if (!/^\d{12}$/.test(phone)) {
    return "Please enter a valid 10-digit phone number";
  }
  return "";
}

function getStatusClass(status: string) {
  let className = "";
  if (status === "approved") {
    className = className + "font-bold text-green-500";
  }
  return className;
}

function getStatusClassName(status: string): any {
  if (!status) {
    return "";
  }
  return [
    "success",
    "complete",
    "approved",
    "active",
    "won",
    "in_progress",
    "paid",
    "premium",
    "enterprise",
    "enterprise-plus",
    "vacant"
  ].indexOf(status) >= 0
    ? "green"
    : [
          "rent_due",
          "expired",
          "balance_due",
          "eviction",
          "suspended",
          "archived",
          "lost",
          "failed",
          "overdue",
          "cancelled",
          "test",
          "closed",
          "under_maintenance",
          "sold",
          "on_hold"
        ].indexOf(status) >= 0
      ? "red"
      : "blue";
}

function getPriorityClassName(priority: string): string {
  return ["low"].indexOf(priority) >= 0
    ? "status-badge green-status"
    : ["urgent", "high"].indexOf(priority) >= 0
      ? "status-badge red-status"
      : "status-badge blue-status";
}

function getPlanClass(plan: string) {
  let className = "";
  if (plan === "starter") {
    className = "font-bold text-red-500";
  } else {
    className = "font-bold text-green-500";
  }
  return className;
}

function getRelativeDateTime(dateTimeString: any) {
  // Parse the server time
  let dateTime = moment.utc(dateTimeString);
  dateTime = dateTime.local();

  // Get the current local time
  const now = moment();

  // Calculate the difference in seconds
  const diffInSeconds = now.diff(dateTime, "seconds");

  // Determine the appropriate format based on the difference in seconds
  if (diffInSeconds < 10) {
    return "A few seconds ago";
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes !== 1 ? "s" : ""} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
  } else if (diffInSeconds < 2592000) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days !== 1 ? "s" : ""} ago`;
  } else if (diffInSeconds < 31536000) {
    const months = Math.floor(diffInSeconds / 2592000);
    return `${months} month${months !== 1 ? "s" : ""} ago`;
  } else {
    const years = Math.floor(diffInSeconds / 31536000);
    return `${years} year${years !== 1 ? "s" : ""} ago`;
  }
}

async function fetchProfilePicture(store: any) {
  const result: any = await request(
    store,
    `documents/category/${user(store)?.id}?category=profile_picture`,
    "GET"
  );
  if (result.ok) {
    return result.data;
  }
}

// Payments

async function onPaymentAuthURLCreateAndOpen(
  store: any,
  subscribeArgs: {
    user_id: number;
    first_name: string;
    last_name: string;
    email: string;
    plan_id: number;
    type: string;
    amount?: number;
    discount?: number;
    discount_percentage?: number;
    user_plan_type?: string;
  },
  setError: any
) {
  // Every payment started from the dashboard comes back to the dashboard's own
  // /payment page. The VertexEase site sends "vertexease" instead; the API
  // maps the name to a URL it knows (PayService.callback_url).
  const result: any = await request(store, "pay/", "POST", {
    ...subscribeArgs,
    return_to: "rentalease"
  });
  // console.log(result);
  if (result.ok) {
    if (result.data.authorization_url) {
      window.location.href = result.data.authorization_url;
    }
  } else {
    if (setError) {
      setError(result?.error);
    }
  }
}

// The symbol each currency an agency may bill in is written with. A code with no
// symbol here is shown as the code itself ("TZS 6,500.00"), which reads fine and
// is never wrong — unlike guessing a symbol that belongs to a different currency.
const CURRENCY_SYMBOLS: Record<string, string> = {
  ZAR: "R",
  USD: "$",
  EUR: "€",
  GBP: "£",
  NGN: "₦",
  GHS: "₵",
  KES: "KSh",
  BWP: "P",
  NAD: "N$",
  ZMW: "K",
  MWK: "MK",
  LSL: "L",
  SZL: "E"
};

/**
 * The setting an agency has saved for `name`, or its default.
 *
 * Agency settings come down with the user on /users/data/ in the same row shape
 * as the app-wide ones, so a value is always present — an agency that has never
 * opened the settings form still reads the catalogue default rather than null.
 */
function agencySetting(store: any, name: string): string | null {
  const rows = store?.agency_settings;
  if (!Array.isArray(rows)) return null;
  const row = rows.find((item: any) => item?.setting === name);
  return row ? (row.value ?? null) : null;
}

/** The currency code this agency bills in. Never empty. */
function agencyCurrency(store: any): string {
  return agencySetting(store, "currency") || "ZAR";
}

/**
 * Format an amount for display.
 *
 * `currency` is the agency's ISO code — the API sends it alongside every set of
 * figures it formats, so a tile can never show one agency's amount with
 * another's symbol. Left off, it falls back to rand, which is what every caller
 * that predates per-agency currency meant.
 */
function getCurrencyAmount(amount: number, currency?: string | null) {
  const code = (currency ?? "ZAR").toUpperCase();
  const symbol = CURRENCY_SYMBOLS[code] ?? code;
  return amount || amount === 0
    ? `${symbol} ${Number(amount).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "";
}

function getCurrencySymbol(): CurrencyType {
  const currencySymbol = "ZAR";
  return currencySymbol;
}

export function getConvertedPrice(randAmount: number, currency: CurrencyType) {
  if (currency === "ZAR") return Math.round(randAmount);
  const usd = randAmount / conversionRates.ZAR;
  const converted = usd * conversionRates[currency];
  return Math.round(converted);
}

export function getYearlyPrice(randMonthlyAmount: number, currency: CurrencyType) {
  const monthlyTotal = randMonthlyAmount * 12;
  const discounted = monthlyTotal * (1 - DISCOUNT_PERCENTAGE / 100);
  return getConvertedPrice(discounted, currency);
}

export function getDiscountedAmount(amount: number, discountPercentage?: number) {
  const percentage = discountPercentage ? discountPercentage : DISCOUNT_PERCENTAGE;
  return amount - amount * (1 - percentage / 100);
}

export function getDiscountedPercentage() {
  return DISCOUNT_PERCENTAGE;
}

export function generateCode(prefix: string = "") {
  // Generate a random rental application code
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let rental_application_code = prefix;

  for (let i = 0; i < 8; i++) {
    rental_application_code += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return rental_application_code;
}

export function isLoggedIn(store: any): boolean {
  return store?.access_token;
}

export async function uploadFile(store: any, file: File, overrideURL?: string): Promise<string> {
  const formData = new FormData();
  formData.append("file", file);
  const result: any = await request(store, overrideURL ? overrideURL : "upload", "POST", formData);
  if (result?.ok && result?.data?.url) {
    return result?.data?.url;
  }
  return "";
}

export function addImage(images: string, newImage: string): string {
  // Split, trim, and filter empty items
  const items = images
    ? images
        .split(",")
        .map(item => item.trim())
        .filter(item => item)
    : [];

  // Append the new image
  items.push(newImage);

  // Join back to comma-separated string
  return items.join(", ");
}

export function removeImage(images: string, index: number): string {
  // Convert string to array
  const items = images
    ? images
        .split(",")
        .map(item => item.trim())
        .filter(item => item)
    : [];

  // Remove item at the given index if valid
  if (index >= 0 && index < items.length) {
    items.splice(index, 1);
  }

  // Return the updated string
  return items.join(", ");
}

export function getFiles(images: string): string[] {
  // Split, trim, and filter empty items
  const items = images
    ?.split(",")
    .map(item => item.trim())
    .filter(item => item);
  return items ?? [];
}

export function getEnv() {
  return config.env;
}

export function getTermsURL() {
  let url = "https://dev.rentalease.co.za/legal/terms&conditions/";
  if (getEnv() === "production") {
    url = "https://rentalease.co.za/legal/terms&conditions/";
  }
  return url;
}

export {
  getError,
  generateErrorMessage,
  generateMessage,
  generateInfoMessage,
  isUserAccountAdmin,
  isUserAccountUser,
  isUserTenant,
  isUserAgencyLandlord,
  isUserAdmin,
  isUserSales,
  isUserSuperAdminOrAdmin,
  user,
  profilePicture,
  humanFormatDate,
  humanFormatDateWithDay,
  humanFormatDateTime,
  getMonth,
  getCurrentMonthEndDate,
  titleCase,
  sentenceCase,
  request,
  clone,
  showToast,
  generateID,
  isPremiumPlan,
  isBasicListingPlan,
  isProListingPlan,
  isEnterpriseOrPlusPlan,
  hasReachedPropertiesLimit,
  hasReachedPropertyGroupLimit,
  getPlanDetails,
  getPlanFeatureLimit,
  getPlanData,
  getPlans,
  getPlanFeatureCode,
  getPlanName,
  getPlanFromID,
  validateEmail,
  validatePhone,
  validatePassword,
  validateConfirmPassword,
  validateEmptyField,
  getStatusClass,
  getStatusClassName,
  getPriorityClassName,
  getPlanClass,
  getRelativeDateTime,
  fetchProfilePicture,
  onPaymentAuthURLCreateAndOpen,
  getCurrencyAmount,
  getCurrencySymbol,
  agencySetting,
  agencyCurrency
  // unreadNotificationCount
};
